import React, { useEffect, useMemo } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import { BUTTONS } from './buttons';
import { EditorBody, EditorRoot, Toolbar, ToolbarButton, ToolbarDivider } from './styles';

const DEFAULT_CONTROLS = [
  'title',
  'bold',
  'italic',
  'underline',
  'strikethrough',
  'highlight',
  'undo',
  'redo',
  'link',
  'media',
  'numberList',
  'bulletList',
  'quote',
  'code',
  'clear',
];

/**
 * Tiptap-based RichText editor. Drop-in replacement for the previous mui-rte
 * usage. Reads and writes HTML directly (no draft-js intermediate).
 *
 * Props:
 *   - defaultValue: initial HTML string
 *   - value: initial HTML string (alias for defaultValue; not controlled — only
 *     read on mount and when the editor remounts via the `key` prop)
 *   - onChange(html): called on every edit with the current HTML
 *   - onBlur(): called when the editor loses focus
 *   - readOnly: render in read-only mode (no toolbar)
 *   - placeholder
 *   - controls: array of mui-rte-compatible control names to render
 *   - customControls: array of { name, content, icon, title? } —  clicking
 *     inserts `content` (plain text) at the cursor. Used for symbol pickers.
 */
export const RichText = ({
  defaultValue,
  value,
  onChange,
  onBlur,
  readOnly = false,
  placeholder,
  controls = DEFAULT_CONTROLS,
  customControls = [],
}) => {
  const initialContent = defaultValue ?? value ?? '';

  const extensions = useMemo(
    () => [
      StarterKit,
      Underline,
      Highlight,
      Link.configure({ openOnClick: false, autolink: true }),
      Image,
    ],
    []
  );

  const editor = useEditor({
    extensions,
    content: initialContent,
    editable: !readOnly,
    onUpdate: ({ editor: ed }) => {
      const html = ed.getHTML();
      onChange?.(html);
    },
    onBlur: () => onBlur?.(),
    editorProps: {
      attributes: {
        spellcheck: 'true',
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
      },
    },
  });

  // Keep editable in sync with readOnly changes after mount.
  useEffect(() => {
    if (editor && editor.isEditable === readOnly) {
      editor.setEditable(!readOnly);
    }
  }, [editor, readOnly]);

  if (!editor) {
    return <EditorRoot />;
  }

  const insertCustom = (content) => {
    editor.chain().focus().insertContent(content).run();
  };

  // Filter to the controls actually known to us and de-duplicate.
  const knownControls = controls.filter((name) => name !== 'save' && BUTTONS[name]);

  return (
    <EditorRoot>
      {!readOnly && (
        <Toolbar>
          {knownControls.map((name) => {
            const cfg = BUTTONS[name];
            const active = cfg.isActive ? cfg.isActive(editor) : false;
            const canRun = cfg.canRun ? cfg.canRun(editor) : true;
            return (
              <React.Fragment key={name}>
                <ToolbarButton
                  type="button"
                  onClick={() => cfg.run(editor)}
                  disabled={!canRun}
                  isActive={active}
                  title={cfg.title}
                  aria-label={cfg.title}
                >
                  {cfg.icon}
                </ToolbarButton>
                {cfg.dividerAfter && <ToolbarDivider />}
              </React.Fragment>
            );
          })}
          {customControls.length > 0 && knownControls.length > 0 && <ToolbarDivider />}
          {customControls.map((c) => (
            <ToolbarButton
              type="button"
              key={c.name}
              onClick={() => insertCustom(c.content)}
              title={c.title || c.content}
              aria-label={c.title || c.content}
            >
              {c.icon || c.content}
            </ToolbarButton>
          ))}
        </Toolbar>
      )}
      <EditorBody>
        <EditorContent editor={editor} />
      </EditorBody>
    </EditorRoot>
  );
};

export default RichText;
